import {
  ReactNode,
  forwardRef,
  ReactElement,
  Ref,
} from "react";
import {
  Button,
  Card,
  Dialog,
  Slide,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import { ValidatorForm } from "react-material-ui-form-validator";
import { mobileCardSx, cardSx, cardHeaderSx } from "utils";
import { TransitionProps } from "@mui/material/transitions";
import { useTranslation } from "react-i18next";
import AuturaTextField from "components/inputs/AuturaTextField";
import { Cancel, Send } from "@mui/icons-material";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ResetPasswordDialogProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  email: string;
  setEmail: (email: string) => void;
  fetcherKey: string;
}

const ResetPassword = ({
  open,
  onCancel,
  onSubmit,
  email,
  setEmail,
}: ResetPasswordDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));

  return (
      <Dialog
      open={open}
      TransitionComponent={Transition}
      aria-label={`${t("dialogs.revoke.title", { ns: "permits" })} Dialog`}
      fullScreen={mobile}
      maxWidth="xs"
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
    >
      <ValidatorForm onSubmit={onSubmit} instantValidate={false}>
        <Card sx={mobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              mobile
                ? {
                    display: "flex",
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 1, p: 0.5 }}>
              <Typography
                variant={"h4"}
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: "left",
                }}
              >
                {t("labels.reset_password", { ns: "userSettings" })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <Grid container width={"100%"} spacing={1.5}>
              <Grid size={12}>
                <AuturaTextField
                  autoFocus
                  required
                  label={t("email")}
                  name="email"
                  value={email}
                  type="text"
                  fullWidth
                  validators={["isEmail"]}
                  errorMessages={[t("errors.invalidError", { name: t("email") })]}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
              <Grid size={12}>
                <Stack
                  width="100%"
                  direction="row"
                  spacing={2}
                  justifyContent="flex-end"
                  mt={2}
                  display="flex"
                  alignItems="flex-end"
                  alignContent="flex-end"
                >
                  <Button
                    variant={"outlined"}
                    onClick={onCancel}
                    color={"secondary"}
                    endIcon={<Cancel />}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    variant={"contained"}
                    type="submit"
                    color={"secondary"}
                    endIcon={<Send />}
                    disabled={!email}
                  >
                    {t("send_password_reset_email")}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </ValidatorForm>
    </Dialog>
  );
};

export default ResetPassword;
